import { formatGameDuration, techModifiers, TECH_BY_ID, type ResearchState } from '@galaxis/shared';
import type { TechArea } from '@galaxis/shared';

export type TechClient = ResearchState['techs'][number];
export type AreaClient = ResearchState['areas'][number];

export const AREA_META: Record<TechArea, { label: string; icon: string; tone: string; ring: string; blurb: string }> = {
  physics: { label: 'Fizika', icon: '⚛️', tone: 'text-sky-300', ring: 'border-sky-500/50', blurb: 'Energia, laborok, szenzorok, hajtóművek, pajzsok' },
  society: { label: 'Társadalom', icon: '🏛️', tone: 'text-emerald-300', ring: 'border-emerald-500/50', blurb: 'Fenntartás, kereskedelem, diplomácia, logisztika' },
  engineering: { label: 'Mérnöki', icon: '🛠️', tone: 'text-orange-300', ring: 'border-orange-500/50', blurb: 'Bányák, raktárak, hajók, fegyverek, páncél' },
};

export const AREAS: TechArea[] = ['physics', 'society', 'engineering'];

export const RARITY_LABEL = { common: 'Gyakori', rare: 'Ritka' } as const;
export const KIND_LABEL = { spine: 'Gerinc', card: 'Kártya' } as const;

/** Border + glow of a card by rarity (the label is always written out too: colour is never the only cue). */
export function rarityClass(t: Pick<TechClient, 'rarity' | 'discoveryOnly'>): string {
  if (t.discoveryOnly) return 'border-amber-400/80 shadow-[0_0_12px_rgba(251,191,36,0.25)]';
  return t.rarity === 'rare' ? 'border-violet-400/80 shadow-[0_0_12px_rgba(167,139,250,0.25)]' : 'border-slate-600';
}

export const fmtDuration = (ms: number) => (ms <= 0 ? 'kész' : formatGameDuration(ms));
export const num = (n: number) => (Math.round(n * 10) / 10).toString().replace('.', ',');
export const thousands = (n: number) => Math.round(n).toLocaleString('hu-HU');

/** A research that costs this much metal asks for a confirmation before it is paid. */
export const EXPENSIVE_METAL = 1100;

/** Why the Start button is disabled (null = it can be pressed). */
export function startBlock(t: TechClient, area: AreaClient, live: { metal: number; energy: number } | null): string | null {
  const c = t.effectiveCost;
  const willQueue = area.slots.used >= area.slots.total || area.queue.length > 0;
  if (willQueue && area.queue.length >= area.queueLimit) return 'A terület várólistája megtelt';
  if (willQueue) return null;
  if (area.rp.balance < c.rp) return `Nincs elég RP ebben a területben (${Math.floor(area.rp.balance)} / ${c.rp})`;
  if (!live || live.metal < c.metal || live.energy < c.energy) return 'Nincs elég nyersanyag';
  return null;
}

/** The effect of a storage technology on the capacity, for the card: [now, after] (null if it is no storage tech). */
export function storagePreview(t: TechClient, state: ResearchState, metalCap: number | null): { from: number; to: number } | null {
  const add = TECH_BY_ID.get(t.id)?.effects.storage;
  if (!add || metalCap === null || t.status === 'done') return null;
  const done = state.techs.filter(x => x.status === 'done').map(x => x.id);
  const have = techModifiers(done).storage;
  return { from: metalCap, to: Math.round(metalCap * (1 + have + add) / (1 + have)) };
}
